import type * as React from 'react';
import {
  createRootRoute, createRoute, createRouter, Outlet, useRouterState,
} from '@tanstack/react-router';
import { AppShell } from '../components/layout/AppShell.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { AccountsPage } from '../features/accounts/AccountsPage.js';
import { AccountDetailPage } from '../features/accounts/AccountDetailPage.js';
import { ExtensionTypesPage } from '../features/products/ExtensionTypesPage.js';
import { CallPlansPage } from '../features/products/CallPlansPage.js';
import { CarriersPage } from '../features/carriers/CarriersPage.js';
import { ExtensionsPage } from '../features/extensions/ExtensionsPage.js';
import { CdrsPage } from '../features/cdrs/CdrsPage.js';
import { BillingPage, RateSheetsPage } from '../features/billing/BillingPage.js';
import { PlaceholderPage } from '../features/misc/PlaceholderPage.js';
import { ManageAccountPage } from '../features/manage/ManageAccountPage.js';

/**
 * The root renders the console shell for every route EXCEPT /manage/*.
 *
 * Account configuration is a full-window working session on one customer, so it
 * gets its own chrome and its own browser tab. Wrapping it in the console's
 * global navigation would invite wandering out of a half-finished change.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (path.startsWith('/manage/')) return <Outlet />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const route = (path: string, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

/**
 * Route tree mirrors the navigation, and per-account detail lives under
 * /accounts/$accountId rather than as top-level screens.
 *
 * That is what makes a support link unambiguous: the URL names the customer, so
 * pasting one cannot land a colleague on a different account than the one being
 * discussed.
 */
const routeTree = rootRoute.addChildren([
  route('/', DashboardPage),
  route('/activity', () => <PlaceholderPage title="Activity log" phase="Phase 2" />),

  // Accounts, and everything about one account behind its id.
  route('/accounts', AccountsPage),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/$accountId',
    component: AccountDetailPage,
  }),
  // Full account configuration — opened in its own browser tab from the console.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/$tenantId',
    component: ManageAccountPage,
  }),

  route('/resellers', () => <PlaceholderPage title="Resellers" phase="Phase 2" />),
  route('/users', () => <PlaceholderPage title="Users & invitations" phase="Phase 2" />),

  // Products
  route('/products/extension-types', ExtensionTypesPage),
  route('/products/call-plans', CallPlansPage),
  route('/products/rate-sheets', RateSheetsPage),

  // Inventory
  route('/inventory/carriers', CarriersPage),
  route('/inventory/numbers', () => <PlaceholderPage title="DID numbers" phase="Phase 2" />),

  // Billing
  route('/billing/runs', () => <PlaceholderPage title="Billing runs" phase="Phase 1J" />),
  route('/billing/invoices', () => <PlaceholderPage title="Invoices" phase="Phase 1J" />),

  // Platform
  route('/platform/nodes', () => <PlaceholderPage title="Nodes & fleet" phase="Phase 2" />),
  route('/platform/settings', () => <PlaceholderPage title="Settings" phase="Phase 2" />),

  // Tenant-scoped users: their own PBX, no account list to drill through.
  route('/my/extensions', ExtensionsPage),
  route('/my/calls', CdrsPage),
  route('/my/billing', BillingPage),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
